import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { BarChart3, Loader2, TrendingUp } from "lucide-react";
import { API } from "@/context/AuthContext";
import {
  getCandidateOrigin,
  ORIGIN_COLOR_HEX,
} from "@/lib/candidateOrigin";

// Distinct color per known channel — kept for the legacy API path (Dashboard)
// where rows come back keyed by lowercase `source` (whatsapp, qr, email, ...).
const CHANNEL_COLORS = {
  whatsapp: "#25D366",
  linkedin: "#0A66C2",
  instagram: "#E4405F",
  facebook: "#1877F2",
  email: "#64748b",
  qr: "#0f172a",
  qr_print: "#0f172a",
  qr_scan: "#0f172a",
  link: "#94a3b8",
  share: "#94a3b8",
  direct: "#94a3b8",
  "": "#cbd5e1",
};

const colorFor = (src) => CHANNEL_COLORS[(src || "").toLowerCase()] || "#1d70d6";

// Status considered "contacted or further along" for the success indicator
const CONTACTED_STATUSES = new Set([
  "contatado",
  "em_entrevista",
  "aprovado",
  "contratado",
]);

/**
 * Compute the origin breakdown CLIENT-SIDE from the candidates array using
 * the same `getCandidateOrigin` logic as `<CandidateOriginBadge>` (LinkedIn,
 * Upload CV, Página pública, Catho, etc.).
 *
 * Returns: { total, breakdown: [{ key, label, color, icon, count, contacted, percentage }] }
 */
const computeBreakdown = (candidates) => {
  const buckets = new Map(); // key = origin.label
  for (const c of candidates) {
    const origin = getCandidateOrigin(c);
    if (!origin) continue;
    const key = origin.label;
    if (!buckets.has(key)) {
      buckets.set(key, {
        key,
        label: origin.label,
        color: origin.color,
        icon: origin.icon,
        count: 0,
        contacted: 0,
      });
    }
    const b = buckets.get(key);
    b.count += 1;
    if (CONTACTED_STATUSES.has(c.status)) b.contacted += 1;
  }

  const total = candidates.length;
  const breakdown = Array.from(buckets.values())
    .map((b) => ({
      ...b,
      percentage: total ? Math.round((b.count / total) * 100) : 0,
    }))
    .sort((a, b) => b.count - a.count);

  return { total, breakdown };
};

/**
 * Reusable source breakdown widget.
 *
 * Two modes:
 *  1. `candidates` prop provided → client-side breakdown using
 *     getCandidateOrigin (LinkedIn / Upload CV / Página pública / Catho …)
 *     ➜ used inside JobDetailPage where we already have the full candidate
 *     list and want to count ALL of them (not just UTM-tracked ones).
 *  2. `endpoint` prop only → fetches from `/dashboard/sources` or
 *     `/dashboard/jobs/{id}/sources` — legacy behaviour for the global
 *     dashboard widget which only sees UTM-tracked public applications.
 */
const SourceBreakdownPanel = ({
  candidates = null, // when truthy: client-side computation
  endpoint = "/dashboard/sources",
  title = "Origem dos candidatos",
  description = "De onde vieram as candidaturas",
  emptyHint = "Quando candidatos aplicarem via links com tracking (WhatsApp, LinkedIn, QR, etc.) você verá o desempenho por canal aqui.",
}) => {
  const useLocal = Array.isArray(candidates);

  // ── Mode A: client-side computation ──────────────────────────────
  const local = useMemo(() => {
    if (!useLocal) return null;
    return computeBreakdown(candidates);
  }, [useLocal, candidates]);

  // ── Mode B: fetch from API ───────────────────────────────────────
  const [remoteData, setRemoteData] = useState(null);
  const [loading, setLoading] = useState(!useLocal);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (useLocal) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}${endpoint}?days=${days}`);
        if (!cancelled) setRemoteData(res.data);
      } catch {
        if (!cancelled) setRemoteData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [useLocal, endpoint, days]);

  // ── Render dispatch ──────────────────────────────────────────────
  if (useLocal) {
    return renderLocal({ title, description, emptyHint, ...local });
  }
  return renderRemote({
    title,
    description,
    emptyHint,
    loading,
    days,
    setDays,
    data: remoteData,
  });
};

// ─── MODE A: modern grid view (client-side, by getCandidateOrigin) ──
const renderLocal = ({ title, description, emptyHint, total, breakdown }) => {
  return (
    <section
      data-testid="source-breakdown-panel"
      className="bg-white border border-slate-200 rounded-lg p-5"
    >
      <div className="flex items-start gap-2 mb-4">
        <div className="w-9 h-9 rounded-md bg-blue-50 text-brand-blue flex items-center justify-center shrink-0">
          <BarChart3 className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="heading text-base font-semibold text-slate-900 leading-tight">
            {title}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">{description}</p>
        </div>
      </div>

      {total === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
          <TrendingUp className="w-6 h-6 text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-slate-600">{emptyHint}</p>
        </div>
      ) : (
        <>
          {/* Hero total */}
          <div className="flex items-baseline gap-2 mb-3">
            <span
              data-testid="total-candidates"
              className="heading text-3xl font-bold text-slate-900 tracking-tight"
            >
              {total}
            </span>
            <span className="text-xs text-slate-500">
              candidato{total !== 1 ? "s" : ""} • {breakdown.length} origen
              {breakdown.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Stacked horizontal distribution bar */}
          <div
            className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden flex mb-5"
            role="img"
            aria-label="Distribuição por origem"
          >
            {breakdown.map((r) => (
              <div
                key={r.key}
                className="h-full transition-all"
                style={{
                  width: `${r.percentage}%`,
                  backgroundColor: ORIGIN_COLOR_HEX[r.color] || "#64748b",
                }}
                title={`${r.label}: ${r.count} (${r.percentage}%)`}
              />
            ))}
          </div>

          {/* Grid of origin cards */}
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
            data-testid="origin-cards-grid"
          >
            {breakdown.map((r) => {
              const Icon = r.icon;
              const hex = ORIGIN_COLOR_HEX[r.color] || "#64748b";
              return (
                <div
                  key={r.key}
                  data-testid={`origin-card-${r.label}`}
                  className="relative bg-slate-50/60 border border-slate-200 rounded-lg p-3.5 hover:border-slate-300 hover:bg-white transition-colors"
                >
                  {/* Accent bar at the top */}
                  <div
                    className="absolute top-0 left-0 right-0 h-0.5 rounded-t-lg"
                    style={{ backgroundColor: hex }}
                  />
                  <div className="flex items-start justify-between mb-2">
                    <div
                      className="w-8 h-8 rounded-md flex items-center justify-center"
                      style={{
                        backgroundColor: `${hex}1a`, // 10% alpha
                        color: hex,
                      }}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-[10px] font-bold text-slate-400">
                      {r.percentage}%
                    </span>
                  </div>
                  <div className="heading text-2xl font-bold text-slate-900 leading-tight">
                    {r.count}
                  </div>
                  <div className="text-xs font-medium text-slate-600 mt-0.5">
                    {r.label}
                  </div>
                  {r.contacted > 0 && (
                    <div
                      className="text-[10px] text-emerald-600 font-medium mt-1.5"
                      title="Candidatos desta origem em contato, entrevista ou aprovados"
                    >
                      {r.contacted} em andamento
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
};

// ─── MODE B: legacy API-driven bar list (used by Dashboard) ─────────
const renderRemote = ({
  title,
  description,
  emptyHint,
  loading,
  days,
  setDays,
  data,
}) => {
  const breakdown = data?.breakdown || [];
  const total = data?.total_public_window || 0;
  const max = breakdown.reduce((acc, r) => Math.max(acc, r.count), 0);

  return (
    <section
      data-testid="source-breakdown-panel"
      className="bg-white border border-slate-200 rounded-lg p-5"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div className="flex items-start gap-2">
          <div className="w-9 h-9 rounded-md bg-blue-50 text-brand-blue flex items-center justify-center shrink-0">
            <BarChart3 className="w-4 h-4" />
          </div>
          <div>
            <h2 className="heading text-base font-semibold text-slate-900 leading-tight">
              {title}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">{description}</p>
          </div>
        </div>
        <div
          className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5 text-[11px]"
          data-testid="window-toggle"
        >
          {[7, 30, 90].map((n) => (
            <button
              type="button"
              key={n}
              onClick={() => setDays(n)}
              data-testid={`window-${n}d`}
              className={`px-2.5 py-1 rounded font-medium transition-colors ${
                days === n
                  ? "bg-white text-brand-blue shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {n}d
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando origem dos candidatos…
        </div>
      ) : total === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
          <TrendingUp className="w-6 h-6 text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-slate-600">{emptyHint}</p>
        </div>
      ) : (
        <div>
          <div className="flex items-baseline gap-2 mb-3">
            <span
              data-testid="total-public-window"
              className="heading text-2xl font-bold text-slate-900"
            >
              {total}
            </span>
            <span className="text-xs text-slate-500">
              candidatura{total !== 1 ? "s" : ""} pública{total !== 1 ? "s" : ""} nos últimos{" "}
              {days} dias
            </span>
          </div>
          <ul className="space-y-2">
            {breakdown.map((r) => (
              <li
                key={r.source || "_unknown"}
                data-testid={`source-row-${r.source || "_unknown"}`}
                className="flex items-center gap-3"
              >
                <div className="flex-shrink-0 w-32 text-xs font-medium text-slate-700 truncate">
                  {r.label}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="h-6 bg-slate-100 rounded-md overflow-hidden relative">
                    <div
                      className="h-full rounded-md flex items-center px-2 transition-all"
                      style={{
                        width: max ? `${(r.count / max) * 100}%` : "0%",
                        backgroundColor: colorFor(r.source),
                        minWidth: "1.5rem",
                      }}
                    >
                      <span className="text-[11px] font-semibold text-white drop-shadow-sm">
                        {r.count}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0 w-20 text-right text-[11px] text-slate-500">
                  <span className="font-semibold text-slate-700">{r.percentage}%</span>
                  {r.contacted > 0 && (
                    <span
                      className="block text-emerald-600 mt-0.5"
                      title="Contatados / em entrevista / aprovados nesta origem"
                    >
                      {r.contacted} ✓
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {data?.total_public_all_time > total && (
            <p className="text-[10px] text-slate-400 mt-3">
              Histórico total: {data.total_public_all_time} candidaturas públicas (de {data.total_any_all_time} total).
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default SourceBreakdownPanel;
